import { useQuery } from "@tanstack/react-query";
import { API_URL, fetchWithTimeout, getPathScope, throwIfNotOk } from "./core";
import { createRestMutation } from "./hook-factory";

export interface SandboxItem {
	id: string;
	name: string;
	providerKind: string;
	/** True when the provider's required credential actually exists in the vault. */
	connected: boolean;
	/** Non-secret identifier fields for display (e.g. teamId, projectId). */
	details?: Record<string, string>;
	config: Record<string, unknown>;
	createdAt: string | null;
	updatedAt: string | null;
}

export interface SandboxProviderCatalogItem {
	id: string;
	credentialFields: Array<{
		key: string;
		label: string;
		required: boolean;
		secret: boolean;
	}>;
}

interface SandboxesPayload {
	builtin: { id: string; kind: "builtin"; availableInCloud: boolean };
	sandboxes: SandboxItem[];
	/** Connectable runtime provider kinds (vercel, …). */
	availableProviders: string[];
	/** Non-secret form metadata declared by the gateway runtime registry. */
	providerCatalog: SandboxProviderCatalogItem[];
}

/** GET /api/:org/sandboxes — builtin + provider-backed sandboxes. */
export function useSandboxes(options?: { enabled?: boolean }) {
	const { slug } = getPathScope();
	return useQuery({
		queryKey: ["sandboxes", slug],
		queryFn: async (): Promise<SandboxesPayload> => {
			const res = await fetchWithTimeout(`${API_URL}/api/${slug}/sandboxes`, {
				credentials: "include",
			});
			await throwIfNotOk(res, "Failed to load sandboxes");
			return (await res.json()) as SandboxesPayload;
		},
		enabled: !!slug && (options?.enabled ?? true),
		staleTime: 30_000,
	});
}

export const useCreateSandbox = createRestMutation<
	[],
	{
		name: string;
		provider_kind: string;
		credential?: Record<string, string>;
	},
	{ sandbox: SandboxItem }
>({
	method: "POST",
	url: ({ slug }) => `${API_URL}/api/${slug}/sandboxes`,
	body: (params) => params,
	invalidate: () => [["sandboxes"]],
	successMessage: "Sandbox created",
	errorMessage: "Failed to create sandbox",
});

export const useDeleteSandbox = createRestMutation<
	[],
	{ id: string },
	{ success: true }
>({
	method: "DELETE",
	url: ({ slug, params }) => `${API_URL}/api/${slug}/sandboxes/${params.id}`,
	invalidate: () => [["sandboxes"]],
	successMessage: "Sandbox deleted",
	errorMessage: "Failed to delete sandbox",
});
